import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Collapse,
  IconButton,
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMsal } from '@azure/msal-react';
import { Pie, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { getApiBaseUrl, getAppBaseUrl } from "../utils/baseUrl";
import { acquireTokenSafe } from '../App';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

interface XeroInvoice {
  invoiceID: string;
  invoiceNumber: string;
  reference: string;
  contact: {
    name: string;
  };
  date: string;
  dueDate: string;
  amountPaid: number;
  amountDue: number;
  status: string;
  total: number;
}

interface InvoiceReport {
  name: string;
  date: string;
  timestamp: string;
  invoiceCount: number;
  invoices: XeroInvoice[];
}

const DraftXeroInvoices: React.FC = () => {
  const { instance } = useMsal();
  const [invoices, setInvoices] = useState<XeroInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string; details?: any } | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof XeroInvoice | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [reports, setReports] = useState<InvoiceReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<InvoiceReport | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    apiUrl: '',
    appBaseUrl: '',
    expectedRedirectUri: '',
    windowLocation: '',
    environment: '',
  });

  const apiUrl = getApiBaseUrl();
  const appBaseUrl = getAppBaseUrl();

  useEffect(() => {
    // Initialize debug info
    setDebugInfo({
      apiUrl,
      appBaseUrl,
      expectedRedirectUri: `${appBaseUrl}/api/xero/callback`,
      windowLocation: window.location.href,
      environment: process.env.NODE_ENV || 'unknown',
    });

    checkXeroAuth();
    fetchReports();

    // Check for OAuth error in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get("error");
    if (oauthError) {
      console.error('OAuth Error from URL:', decodeURIComponent(oauthError));
      setError({
        message: `OAuth Error: ${decodeURIComponent(oauthError)}`,
        details: { source: 'URL Params', raw: oauthError },
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkXeroAuth = async () => {
    try {
      console.log("=== FRONTEND XERO AUTH CHECK ===");
      console.log("API URL:", apiUrl);
      console.log("App Base URL:", appBaseUrl);
      console.log("Expected Redirect URI:", `${appBaseUrl}/api/xero/callback`);

      // Get token using MSAL
      const accounts = instance.getAllAccounts();
      console.log("MSAL Accounts:", accounts.length > 0 ? `${accounts.length} accounts found` : 'No accounts');
      if (accounts.length === 0) {
        console.log("No MSAL accounts available");
        setIsAuthorized(false);
        return;
      }

      const token = await acquireTokenSafe(instance, accounts);
      if (!token) { setIsAuthorized(false); return; }
      console.log("MSAL Token Acquired");

      const response = await fetch(`${apiUrl}/api/xero/auth-status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Debug-Request': 'auth-status',
        },
      });

      console.log("Auth Status Response:", {
        status: response.status,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Auth Status Data:", data);
        setIsAuthorized(data.isAuthorized);
        if (data.isAuthorized) {
          console.log("Authorization confirmed - fetching invoices");
          fetchInvoices();
        } else {
          console.log("Not authorized - showing auth screen");
        }
      } else {
        const errorText = await response.text();
        console.error("Auth Status Error:", {
          status: response.status,
          errorText,
        });
        setError({
          message: `Auth status check failed (Status: ${response.status})`,
          details: { errorText, status: response.status },
        });
      }
      console.log("=== END FRONTEND AUTH CHECK ===");
    } catch (error: any) {
      console.error("Error checking auth status:", {
        message: error.message,
        stack: error.stack,
      });
      setIsAuthorized(false);
      setError({
        message: "Failed to check Xero auth status",
        details: { error: error.message, stack: error.stack },
      });
    }
  };

  const initiateXeroAuth = async () => {
    try {
      console.log("=== INITIATING XERO OAUTH ===");
      console.log("API URL:", apiUrl);
      console.log("App Base URL:", appBaseUrl);
      console.log("Redirect URI:", `${appBaseUrl}/api/xero/callback`);

      // Get token using MSAL
      const accounts = instance.getAllAccounts();
      console.log("MSAL Accounts:", accounts.length > 0 ? `${accounts.length} accounts found` : 'No accounts');
      if (accounts.length === 0) {
        console.log("No MSAL accounts available for Xero auth");
        return;
      }
      const token = await acquireTokenSafe(instance, accounts);
      if (!token) return;
      console.log("MSAL Token Acquired");

      const response = await fetch(`${apiUrl}/api/xero/auth-url`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Debug-Request': 'auth-url',
        },
      });

      console.log("Auth URL Response:", {
        status: response.status,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Auth URL Data:", data);
        console.log("Redirecting to Xero OAuth URL:", data.authUrl);
        window.location.href = data.authUrl;
      } else {
        const errorData = await response.json();
        console.error("Auth URL Error:", errorData);
        setError({
          message: "Failed to get Xero auth URL",
          details: errorData,
        });
      }
      console.log("=== END XERO OAUTH INITIATION ===");
    } catch (error: any) {
      console.error("Error initiating Xero auth:", {
        message: error.message,
        stack: error.stack,
      });
      setError({
        message: "Failed to initiate Xero authorization",
        details: { error: error.message, stack: error.stack },
      });
    }
  };

  const fetchReports = async () => {
    try {
      console.log("=== FETCHING INVOICE REPORTS ===");
      const accounts = instance.getAllAccounts();
      console.log("MSAL Accounts:", accounts.length > 0 ? `${accounts.length} accounts found` : 'No accounts');
      if (accounts.length === 0) {
        console.log("No MSAL accounts available for fetching reports");
        return;
      }

      const token = await acquireTokenSafe(instance, accounts);
      if (!token) return;
      console.log("MSAL Token Acquired");

      const response = await fetch(`${apiUrl}/api/xero/invoice-reports`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Debug-Request': 'invoice-reports',
        },
      });

      console.log("Invoice Reports Response:", {
        status: response.status,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Fetched Reports:", data);
        setReports(data);
        if (data.length > 0 && !selectedReport) {
          setSelectedReport(data[0]);
          setInvoices(data[0].invoices);
        }
      } else {
        const errorText = await response.text();
        console.error("Invoice Reports Error:", {
          status: response.status,
          errorText,
        });
        setError({
          message: `Failed to fetch reports (Status: ${response.status})`,
          details: { errorText, status: response.status },
        });
      }
      console.log("=== END FETCHING INVOICE REPORTS ===");
    } catch (error: any) {
      console.error("Error fetching reports:", {
        message: error.message,
        stack: error.stack,
      });
      setError({
        message: "Failed to fetch invoice reports",
        details: { error: error.message, stack: error.stack },
      });
    }
  };

  const fetchInvoices = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      console.log("=== FETCHING XERO INVOICES ===");
      console.log("Force Refresh:", forceRefresh);
      const accounts = instance.getAllAccounts();
      console.log("MSAL Accounts:", accounts.length > 0 ? `${accounts.length} accounts found` : 'No accounts');
      if (accounts.length === 0) {
        console.log("No MSAL accounts available");
        setError({ message: "Not authenticated with MSAL" });
        setLoading(false);
        return;
      }

      const token = await acquireTokenSafe(instance, accounts);
      if (!token) {
        setError({ message: "Not authenticated" });
        setLoading(false);
        return;
      }
      console.log("MSAL Token Acquired");

      const queryParams = forceRefresh ? '?forceRefresh=true' : '';
      const response = await fetch(`${apiUrl}/api/xero/draft-invoices${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Debug-Request': 'draft-invoices',
        },
      });

      console.log("Draft Invoices Response:", {
        status: response.status,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Draft Invoices Data:", data);
        setInvoices(data);
        fetchReports();
        setSnackbarMessage('Invoices fetched successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else if (response.status === 401) {
        console.log("Unauthorized - setting isAuthorized to false");
        setIsAuthorized(false);
        setError({ message: "Xero authorization required" });
      } else {
        const errorData = await response.json();
        console.error("Draft Invoices Error:", errorData);
        setError({
          message: errorData.error || "Failed to fetch invoices",
          details: errorData,
        });
      }
    } catch (error: any) {
      console.error("Error fetching invoices:", {
        message: error.message,
        stack: error.stack,
      });
      setError({
        message: "Failed to fetch invoices",
        details: { error: error.message, stack: error.stack },
      });
    } finally {
      setLoading(false);
      console.log("=== END FETCHING XERO INVOICES ===");
    }
  };

  // Copy debug info to clipboard
  const copyDebugInfo = () => {
    const debugText = `
Debug Information:
- API URL: ${debugInfo.apiUrl}
- App Base URL: ${debugInfo.appBaseUrl}
- Expected Redirect URI: ${debugInfo.expectedRedirectUri}
- Window Location: ${debugInfo.windowLocation}
- Environment: ${debugInfo.environment}
- Error: ${JSON.stringify(error, null, 2)}
- Timestamp: ${new Date().toISOString()}
    `;
    navigator.clipboard.writeText(debugText);
    setSnackbarMessage('Debug info copied to clipboard');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Filter and search invoices
  const filteredInvoices = useMemo(() => {
    const currentInvoices = selectedReport ? selectedReport.invoices : invoices;
    return currentInvoices.filter((invoice) => {
      const matchesSearch =
        invoice.invoiceNumber
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        invoice.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.contact?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [selectedReport, invoices, searchTerm, statusFilter]);

  // Sorted invoices
  const sortedInvoices = useMemo(() => {
    if (!sortConfig.key) return filteredInvoices;

    return [...filteredInvoices].sort((a, b) => {
      const aValue = sortConfig.key === 'contact' ? a.contact?.name : a[sortConfig.key!];
      const bValue = sortConfig.key === 'contact' ? b.contact?.name : b[sortConfig.key!];

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      // Handle date sorting
      if (sortConfig.key === 'date' || sortConfig.key === 'dueDate') {
        const aDate = new Date(aValue as string).getTime();
        const bDate = new Date(bValue as string).getTime();
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredInvoices, sortConfig]);

  // Paginated invoices
  const paginatedInvoices = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedInvoices.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedInvoices, page, rowsPerPage]);

  // Handle sorting
  const handleSort = (key: keyof XeroInvoice | 'contact') => {
    setSortConfig(prevConfig => ({
      key: key as keyof XeroInvoice,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Calculate KPIs
  const kpis = useMemo(() => {
    const currentInvoices = selectedReport ? selectedReport.invoices : invoices;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Previous month calculation
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const previousMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    // Current fiscal year (April 1st to March 31st)
    const fiscalYearStart = new Date(
      now.getMonth() >= 3 ? currentYear : currentYear - 1,
      3,
      1,
    );
    const fiscalYearEnd = new Date(
      now.getMonth() >= 3 ? currentYear + 1 : currentYear,
      2,
      31,
    );

    // Current quarter (based on fiscal year)
    const monthsSinceFiscalStart = (now.getMonth() - 3 + 12) % 12;
    const currentFiscalQuarter = Math.floor(monthsSinceFiscalStart / 3) + 1;
    const quarterStart = new Date(fiscalYearStart);
    quarterStart.setMonth(
      quarterStart.getMonth() + (currentFiscalQuarter - 1) * 3,
    );
    const quarterEnd = new Date(quarterStart);
    quarterEnd.setMonth(quarterEnd.getMonth() + 3);
    quarterEnd.setDate(0); // Last day of previous month

    // Helper function to calculate totals by status and period
    const calculateByStatus = (status: string) => {
      const statusInvoices = currentInvoices.filter((inv) => inv.status === status);

      const thisMonthInvoices = statusInvoices.filter((inv) => {
        const invDate = new Date(inv.date);
        return (
          invDate.getMonth() === currentMonth &&
          invDate.getFullYear() === currentYear
        );
      });

      const previousMonthInvoices = statusInvoices.filter((inv) => {
        const invDate = new Date(inv.date);
        return (
          invDate.getMonth() === previousMonth &&
          invDate.getFullYear() === previousMonthYear
        );
      });

      const thisMonth = thisMonthInvoices.reduce((sum, inv) => sum + inv.total, 0);
      const previousMonthTotal = previousMonthInvoices.reduce((sum, inv) => sum + inv.total, 0);

      const thisQuarter = statusInvoices
        .filter((inv) => {
          const invDate = new Date(inv.date);
          return invDate >= quarterStart && invDate <= quarterEnd;
        })
        .reduce((sum, inv) => sum + inv.total, 0);

      const thisFY = statusInvoices
        .filter((inv) => {
          const invDate = new Date(inv.date);
          return invDate >= fiscalYearStart && invDate <= fiscalYearEnd;
        })
        .reduce((sum, inv) => sum + inv.total, 0);

      return {
        thisMonth,
        thisMonthCount: thisMonthInvoices.length,
        previousMonth: previousMonthTotal,
        previousMonthCount: previousMonthInvoices.length,
        thisQuarter,
        thisFY,
        count: statusInvoices.length,
      };
    };

    return {
      draft: calculateByStatus("DRAFT"),
      authorised: calculateByStatus("AUTHORISED"),
      paid: calculateByStatus("PAID"),
      totalInvoices: currentInvoices.length,
    };
  }, [selectedReport, invoices]);

  const statusDistribution = useMemo(() => {
    const currentInvoices = selectedReport ? selectedReport.invoices : invoices;
    const statusCounts = currentInvoices.reduce(
      (acc, invoice) => {
        acc[invoice.status] = (acc[invoice.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const labels = Object.keys(statusCounts);
    const data = Object.values(statusCounts);
    const backgroundColor = [
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#8884D8",
      "#82CA9D",
    ];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 1,
        },
      ],
    };
  }, [selectedReport, invoices]);

  const statusAmounts = useMemo(() => {
    const currentInvoices = selectedReport ? selectedReport.invoices : invoices;
    const statusTotals = currentInvoices.reduce(
      (acc, invoice) => {
        acc[invoice.status] = (acc[invoice.status] || 0) + invoice.total;
        return acc;
      },
      {} as Record<string, number>,
    );

    const labels = Object.keys(statusTotals);
    const data = Object.values(statusTotals);
    const backgroundColor = [
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#8884D8",
      "#82CA9D",
    ];

    return {
      labels,
      datasets: [
        {
          label: "Invoice Amount",
          data,
          backgroundColor,
        },
      ],
    };
  }, [selectedReport, invoices]);

  const revenueByWeek = useMemo(() => {
    const currentInvoices = selectedReport ? selectedReport.invoices : invoices;
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Current fiscal year (April 1st to March 31st)
    const fiscalYearStart = new Date(
      now.getMonth() >= 3 ? currentYear : currentYear - 1,
      3,
      1,
    );
    
    // Get all weeks from fiscal year start to now
    const weeks: { start: Date; end: Date; label: string }[] = [];
    const currentDate = new Date(fiscalYearStart);
    
    while (currentDate <= now) {
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      weeks.push({
        start: new Date(weekStart),
        end: new Date(weekEnd),
        label: `${weekStart.getDate()}/${weekStart.getMonth() + 1}`,
      });
      
      currentDate.setDate(currentDate.getDate() + 7);
    }

    // Calculate revenue by week for each status
    const draftData: number[] = [];
    const authorisedData: number[] = [];
    const paidData: number[] = [];

    weeks.forEach((week) => {
      const weekInvoices = currentInvoices.filter((invoice) => {
        const invoiceDate = new Date(invoice.date);
        return invoiceDate >= week.start && invoiceDate <= week.end;
      });

      const draftTotal = weekInvoices
        .filter((inv) => inv.status === "DRAFT")
        .reduce((sum, inv) => sum + inv.total, 0);
      
      const authorisedTotal = weekInvoices
        .filter((inv) => inv.status === "AUTHORISED")
        .reduce((sum, inv) => sum + inv.total, 0);
      
      const paidTotal = weekInvoices
        .filter((inv) => inv.status === "PAID")
        .reduce((sum, inv) => sum + inv.total, 0);

      draftData.push(draftTotal);
      authorisedData.push(authorisedTotal);
      paidData.push(paidTotal);
    });

    return {
      labels: weeks.map((week) => week.label),
      datasets: [
        {
          label: "Draft Revenue",
          data: draftData,
          borderColor: "#ed6c02",
          backgroundColor: "rgba(237, 108, 2, 0.1)",
          tension: 0.1,
        },
        {
          label: "Authorised Revenue",
          data: authorisedData,
          borderColor: "#2e7d32",
          backgroundColor: "rgba(46, 125, 50, 0.1)",
          tension: 0.1,
        },
        {
          label: "Paid Revenue",
          data: paidData,
          borderColor: "#0288d1",
          backgroundColor: "rgba(2, 136, 209, 0.1)",
          tension: 0.1,
        },
      ],
    };
  }, [selectedReport, invoices]);

  const pieChartOptions = {
    plugins: {
      legend: {
        position: "right" as const,
      },
      title: {
        display: true,
        text: "Invoice Status Distribution",
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Amount by Invoice Status",
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Revenue by Week (Current Fiscal Year)",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
      },
    },
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Invoice Number",
      "Reference",
      "Contact",
      "Date",
      "Due Date",
      "Amount Paid",
      "Amount Due",
      "Status",
      "Total",
    ];
    const csvContent = [
      headers.join(","),
      ...sortedInvoices.map((invoice) =>
        [
          `"${invoice.invoiceNumber || ""}"`,
          `"${invoice.reference || ""}"`,
          `"${invoice.contact?.name || ""}"`,
          `"${formatDate(invoice.date)}"`,
          `"${formatDate(invoice.dueDate)}"`,
          invoice.amountPaid,
          invoice.amountDue,
          `"${invoice.status}"`,
          invoice.total,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `xero_invoices_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusChip = (status: string) => {
    const color =
      status === "DRAFT"
        ? "warning"
        : status === "AUTHORISED"
          ? "success"
          : status === "PAID"
            ? "info"
            : "default";

    return <Chip label={status} color={color} size="small" />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IE");
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Xero Inventory
        </Typography>
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            Xero Authorization Required
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            To access Xero invoice data, you need to authorize this application
            with your Xero account.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Current API URL: {apiUrl}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            App Base URL: {appBaseUrl}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            Expected OAuth Redirect: {`${appBaseUrl}/api/xero/callback`}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={initiateXeroAuth}
            size="large"
          >
            Connect to Xero
          </Button>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">{error.message}</Typography>
              {error.details && (
                <Typography variant="caption" component="pre" sx={{ mt: 1 }}>
                  {JSON.stringify(error.details, null, 2)}
                </Typography>
              )}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={copyDebugInfo}
              sx={{ textTransform: 'none' }}
            >
              Copy Debug Info
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: '#1e293b',
            fontSize: '2rem',
          }}
        >
          Xero Inventory
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Report Date</InputLabel>
            <Select
              value={selectedReport?.name || ''}
              label="Report Date"
              onChange={(e) => {
                const report = reports.find(r => r.name === e.target.value);
                setSelectedReport(report || null);
                if (report) {
                  setInvoices(report.invoices);
                }
              }}
            >
              {reports.map(report => (
                <MenuItem key={report.name} value={report.name}>
                  {new Date(report.timestamp).toLocaleDateString()} {new Date(report.timestamp).toLocaleTimeString()}
                  {report === reports[0] && " (Latest)"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            onClick={() => fetchInvoices(true)} 
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            {loading ? <CircularProgress size={20} /> : "Refresh Data"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body1">{error.message}</Typography>
          {error.details && (
            <Typography variant="caption" component="pre" sx={{ mt: 1 }}>
              {JSON.stringify(error.details, null, 2)}
            </Typography>
          )}
        </Alert>
      )}

      {/* Debug Panel */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Debug Information</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={copyDebugInfo}
              sx={{ textTransform: 'none' }}
            >
              Copy Debug Info
            </Button>
            <IconButton onClick={() => setDebugOpen(!debugOpen)}>
              <ExpandMoreIcon sx={{ transform: debugOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </IconButton>
          </Box>
        </Box>
        <Collapse in={debugOpen}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>API URL:</strong> {debugInfo.apiUrl}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>App Base URL:</strong> {debugInfo.appBaseUrl}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Expected Redirect URI:</strong> {debugInfo.expectedRedirectUri}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Window Location:</strong> {debugInfo.windowLocation}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Environment:</strong> {debugInfo.environment}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Current Time:</strong> {new Date().toISOString()}
            </Typography>
            {error && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Error Details:</strong>
                <pre>{JSON.stringify(error, null, 2)}</pre>
              </Typography>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* KPIs */}
      <Box sx={{ mb: 4 }}>
        {/* Draft Invoices KPIs */}
        <Typography variant="h6" sx={{ mb: 2, color: "#ed6c02" }}>
          Draft Invoices
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #ed6c02" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Previous Month
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.draft.previousMonth)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {kpis.draft.previousMonthCount} invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #ed6c02" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Month
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.draft.thisMonth)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {kpis.draft.thisMonthCount} invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #ed6c02" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Quarter
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.draft.thisQuarter)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #ed6c02" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Fiscal Year
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.draft.thisFY)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Authorised Invoices KPIs */}
        <Typography variant="h6" sx={{ mb: 2, color: "#2e7d32" }}>
          Authorised Invoices
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #2e7d32" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Previous Month
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.authorised.previousMonth)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {kpis.authorised.previousMonthCount} invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #2e7d32" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Month
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.authorised.thisMonth)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {kpis.authorised.thisMonthCount} invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #2e7d32" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Quarter
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.authorised.thisQuarter)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #2e7d32" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Fiscal Year
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.authorised.thisFY)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Paid Invoices KPIs */}
        <Typography variant="h6" sx={{ mb: 2, color: "#0288d1" }}>
          Paid Invoices
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #0288d1" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Previous Month
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.paid.previousMonth)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {kpis.paid.previousMonthCount} invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #0288d1" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Month
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.paid.thisMonth)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {kpis.paid.thisMonthCount} invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #0288d1" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Quarter
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.paid.thisQuarter)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card sx={{ borderLeft: "4px solid #0288d1" }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Fiscal Year
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(kpis.paid.thisFY)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Status Distribution
            </Typography>
            <Pie data={statusDistribution} options={pieChartOptions} />
          </Paper>
        </Grid>
        <Grid size={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Amount by Invoice Status
            </Typography>
            <Bar data={statusAmounts} options={barChartOptions} />
          </Paper>
        </Grid>
        <Grid size={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Revenue by Week
            </Typography>
            <Line data={revenueByWeek} options={lineChartOptions} />
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={6}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Search by invoice number, reference, or contact"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="AUTHORISED">Authorised</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('invoiceNumber')}
              >
                Invoice Number {sortConfig.key === 'invoiceNumber' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('reference')}
              >
                Reference {sortConfig.key === 'reference' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('contact')}
              >
                To {sortConfig.key === 'contact' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('date')}
              >
                Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('dueDate')}
              >
                Due Date {sortConfig.key === 'dueDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right" 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('amountPaid')}
              >
                Paid {sortConfig.key === 'amountPaid' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right" 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('amountDue')}
              >
                Due {sortConfig.key === 'amountDue' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right" 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('total')}
              >
                Total {sortConfig.key === 'total' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ color: "#fff", fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSort('status')}
              >
                Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No invoices found
                </TableCell>
              </TableRow>
            ) : (
              paginatedInvoices.map((invoice, index) => (
                <TableRow 
                  key={invoice.invoiceID}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#f5f5f5" : "#fff",
                    "&:hover": {
                      backgroundColor: "#e0f7fa",
                    },
                  }}
                >
                  <TableCell>{invoice.invoiceNumber || "N/A"}</TableCell>
                  <TableCell>{invoice.reference || "N/A"}</TableCell>
                  <TableCell>{invoice.contact?.name || "N/A"}</TableCell>
                  <TableCell>{formatDate(invoice.date)}</TableCell>
                  <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(invoice.amountPaid)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(invoice.amountDue)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(invoice.total)}
                  </TableCell>
                  <TableCell>{getStatusChip(invoice.status)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={sortedInvoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Export CSV Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          onClick={exportToCSV}
          disabled={sortedInvoices.length === 0}
          sx={{
            background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            borderRadius: "25px",
            padding: "8px 20px",
            textTransform: "none",
            fontWeight: "bold",
            boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
            "&:hover": {
              background: "linear-gradient(45deg, #1565c0 30%, #2196f3 90%)",
              boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
            },
          }}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DraftXeroInvoices;